/**
 * Third-party script integrations for analytics and marketing.
 *
 * This file manages Google Analytics and HubSpot script loading and consent.
 * GA uses Consent Mode v2 to respect user privacy preferences.
 */

import type { ConsentOptions, ConsentStatus } from '#nuxt-scripts/registry/google-analytics'
import { DEFAULT_GA_CONSENT_OPTIONS, DEFAULT_PRIVACY_PREFS } from '~/constants'

// ==================
// Google Analytics
// ==================

/** Maps GA consent categories to boolean values for internal state management */
export type GAConsentState = Record<keyof Pick<ConsentOptions, 'ad_user_data' | 'ad_personalization' | 'ad_storage' | 'analytics_storage'>, boolean>

/**
 * Initializes Google Analytics with Consent Mode v2.
 * GA starts with denied consent by default (see DEFAULT_GA_CONSENT_OPTIONS).
 * Use updateConsentOptions() to grant consent after user approval.
 */
export function useGoogleAnalytics() {
  const { proxy } = useScriptGoogleAnalytics({
    id: 'G-LQZPC5MQTC',
    onBeforeGtagStart: (gtag) => {
      // Initialize with denied consent — respects privacy by default
      gtag('consent', 'default', DEFAULT_GA_CONSENT_OPTIONS)
    },
  })

  /**
   * Updates GA consent state. Call this when user changes privacy preferences.
   * @param state - Boolean map of consent categories
   */
  function updateConsentOptions(
    state: MaybeRefOrGetter<PrivacyPrefs>,
  ) {
    proxy.gtag('consent', 'update', convertState(toValue(state)))
  }

  /**
   * Converts internal boolean state to GA's 'granted'/'denied' format.
   */
  function convertState(
    prefs: MaybeRefOrGetter<PrivacyPrefs>,
  ): ConsentOptions {
    const newPrefs = toValue(prefs)

    const gaConsentState: GAConsentState = {
      analytics_storage: newPrefs.analytics,
      ad_user_data: newPrefs.marketing,
      ad_personalization: newPrefs.marketing,
      ad_storage: newPrefs.marketing,
    }

    const gaConsentOptions: ConsentOptions & Record<string, ConsentStatus> = {}

    for (const [key, value] of Object.entries(gaConsentState))
      gaConsentOptions[key] = value ? 'granted' : 'denied'

    return gaConsentOptions
  }

  return {
    proxy,
    updateConsentOptions,
  }
}

// ==================
// HubSpot
// ==================

declare global {
  interface Window {
    _hsp: HubspotApi
    hbspt: HubspotFormsApi
    disableHubSpotCookieBanner: boolean
  }
}

/** HubSpot's _hsp is an array you push command tuples to (not a function like gtag) */
export type HubspotApi = Array<
  | ['addEnabledFeatureGates', unknown[]]
  | ['setBusinessUnitId', number]
  | ['setHubSpotConsent', HSConsentState]
  | ['addPrivacyConsentListener', (consent: any) => void]
>

/** HubSpot Forms API interface (loaded from external script) */
export interface HubspotFormsApi {
  forms: {
    create: (arg: {
      region: string
      portalId: string
      formId: string
      target: string
    }) => void
  }
}

/** HubSpot consent categories */
export interface HSConsentState {
  analytics: boolean
  advertisement: boolean
  functionality: boolean
}

/**
 * Loads the HubSpot tracking script (hs-scripts).
 * This enables HubSpot analytics and tracking on the site.
 * Portal ID: 43699019
 *
 * HubSpot uses an array-based API (_hsp) where you push command tuples.
 * This differs from GA's function-based gtag, so we access window._hsp
 * directly rather than using the useScript proxy pattern.
 */
export function useHubspot() {
  const thePrivacy = useState<PrivacyPrefs>('privacy', () => DEFAULT_PRIVACY_PREFS)

  const { onLoaded, onError } = useScript({
    type: 'text/javascript',
    id: 'hs-script-loader',
    async: true,
    defer: true,
    src: '//js.hs-scripts.com/43699019.js',
    tagPosition: 'bodyClose',
  }, {
    beforeInit: () => {
      if (import.meta.client) {
        // Disable HubSpot's built-in cookie banner (we use our own)
        window.disableHubSpotCookieBanner = true
        // Initialize _hsp array before script loads (HubSpot pattern)
        window._hsp = window._hsp || []
        if (import.meta.dev) {
          window._hsp.push([
            'addPrivacyConsentListener',
            function (consent) {
              console.log('HubSpot Analytics consent:')
              console.log(`analytics: ${consent.categories.analytics}`)
              console.log(`advertisement: ${consent.categories.advertisement}`)
              console.log(`functionality: ${consent.categories.functionality}`)
            },
          ])
        }
      }
    },
  })

  const failure = ref(false)

  onLoaded(() => {
    callOnce('load-hs-consent', () => {
      updateConsentOptions(thePrivacy.value)
    })
  })

  onError(() => {
    failure.value = true
  })

  /**
   * Updates HubSpot consent by pushing to window._hsp array.
   * Can be called before or after script loads (queued commands pattern).
   * @param prefs - Consent state for analytics/ads/functionality
   */
  function updateConsentOptions(prefs: PrivacyPrefs) {
    if (import.meta.client) {
      window._hsp = window._hsp || []
      const consent: HSConsentState = {
        analytics: prefs.analytics,
        advertisement: prefs.marketing,
        functionality: prefs.functionality,
      }
      window._hsp.push(['setHubSpotConsent', consent])
    }
  }

  return {
    failure,
    updateConsentOptions,
  }
}

/**
 * Embeds a HubSpot form into a target element.
 * Loads the HubSpot forms embed script and creates the form when ready.
 *
 * @param formId - HubSpot form ID (from HubSpot dashboard)
 * @returns Object with targetId (DOM ID for form container) and failure (true if script failed)
 */
export function useHubspotForm(formId: string) {
  const targetId = useId()

  const failure = ref(false)

  const { onLoaded, onError } = useScript({
    src: '//js.hsforms.net/forms/embed/v2.js',
    crossorigin: false,
  }, {
    use: () => window.hbspt,
  })

  onLoaded((instance) => {
    instance.forms.create({
      region: 'na1',
      portalId: '43699019',
      formId,
      target: `#${targetId}`,
    })
  })

  onError(() => {
    failure.value = true
  })

  return {
    targetId,
    failure,
  }
}
